import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { safeJoin, validateQuantity } from '../../utils/validation';
import type { PickingListItem, ConfirmPickInput } from '../../types';

interface ConfirmPickModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ConfirmPickInput) => Promise<void>;
  item: PickingListItem | null;
  isLoading?: boolean;
}

export function ConfirmPickModal({
  open,
  onClose,
  onSubmit,
  item,
  isLoading,
}: ConfirmPickModalProps) {
  const [quantityPicked, setQuantityPicked] = useState(0);
  const [notes, setNotes] = useState('');

  // Update quantity when item changes
  useEffect(() => {
    if (item) {
      setQuantityPicked(item.quantity_to_pick || 0);
    }
  }, [item]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item || quantityPicked <= 0) return;

    // Issue #9: Validate quantity bounds
    const validation = validateQuantity(quantityPicked, item.quantity_to_pick);
    if (!validation.valid) {
      toast.error(validation.error || 'Invalid quantity');
      return;
    }

    // Issue #20: Add try-catch for unhandled promise rejections
    // Issue #18: Enhance error messages with details
    try {
      await onSubmit({
        quantity_picked: quantityPicked,
        notes: notes || undefined,
      });

      // Only reset and close on success
      setQuantityPicked(0);
      setNotes('');
      toast.success('Pick confirmed successfully');
      onClose();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to confirm pick: ${message}`);
      console.error('Error confirming pick:', error);
      // Keep form open so user can retry
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Confirm Pick</DialogTitle>
          <DialogDescription>
            Confirm that you have picked this item from the specified location.
          </DialogDescription>
        </DialogHeader>

        {item && (
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">SKU:</span>
              <span className="font-medium">{item.sku_code}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Serial:</span>
              <span className="font-mono text-sm">{safeJoin(item.serial_numbers, ', ')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Location:</span>
              <span className="font-mono text-sm">{item.bin_code || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Requested:</span>
              <span className="font-semibold">{item.quantity_to_pick}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="quantity_picked">Quantity Picked *</Label>
            <Input
              id="quantity_picked"
              type="number"
              min={0}
              max={item?.quantity_to_pick}
              value={quantityPicked}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setQuantityPicked(0);
                  return;
                }
                const parsed = parseInt(value, 10);
                if (!isNaN(parsed) && parsed >= 0) {
                  setQuantityPicked(parsed);
                }
              }}
              className="mt-1"
              required
            />
            {quantityPicked < (item?.quantity_to_pick || 0) && quantityPicked > 0 && (
              <p className="text-sm text-orange-600 mt-1">
                Partial pick: {quantityPicked} of {item?.quantity_to_pick}
              </p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium">Notes (optional)</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes about this pick..."
              className="mt-1"
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Confirming...' : 'Confirm Pick'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
